import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isUserAuthenticated } from '../services/authService';

type AuthState = {
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
    refreshAuth: () => Promise<void>;
    logoutWithAuthProvider: () => void;
};
const AuthContext = createContext<AuthState | null>(null);
export const useAuth = (): AuthState => {
    const auth = useContext(AuthContext);
    if (!auth) throw new Error('useAuth must be used inside an AuthProvider');
    return auth;
};
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);
    const refreshAuth = useCallback(async () => {
        const attempt = ++generation.current;
        setLoading(true);
        try {
            const authenticated = await isUserAuthenticated();
            if (attempt !== generation.current) return;
            setIsAuthenticated(authenticated);
            setError(null);
        } catch {
            if (attempt !== generation.current) return;
            setError('Unable to check your session. Check your connection and try again.');
        } finally {
            if (attempt === generation.current) setLoading(false);
        }
    }, []);
    const logoutWithAuthProvider = useCallback(() => {
        generation.current++;
        setIsAuthenticated(false);
        setError(null);
        setLoading(false);
    }, []);
    useEffect(() => {
        void refreshAuth();
        window.addEventListener('session-ended', logoutWithAuthProvider);
        return () => { generation.current++; window.removeEventListener('session-ended', logoutWithAuthProvider); };
    }, [refreshAuth, logoutWithAuthProvider]);
    return <AuthContext.Provider value={{ isAuthenticated, loading, error, refreshAuth, logoutWithAuthProvider }}>{children}</AuthContext.Provider>;
};
